/*
 * Copyright (C) 2026 Das Land Schleswig-Holstein vertreten durch den
 * Ministerpräsidenten des Landes Schleswig-Holstein
 * Staatskanzlei
 * Abteilung Digitalisierung und zentrales IT-Management der Landesregierung
 *
 * Lizenziert unter der EUPL, Version 1.2 oder - sobald
 * diese von der Europäischen Kommission genehmigt wurden -
 * Folgeversionen der EUPL ("Lizenz");
 * Sie dürfen dieses Werk ausschließlich gemäß
 * dieser Lizenz nutzen.
 * Eine Kopie der Lizenz finden Sie hier:
 *
 * https://joinup.ec.europa.eu/collection/eupl/eupl-text-eupl-12
 *
 * Sofern nicht durch anwendbare Rechtsvorschriften
 * gefordert oder in schriftlicher Form vereinbart, wird
 * die unter der Lizenz verbreitete Software "so wie sie
 * ist", OHNE JEGLICHE GEWÄHRLEISTUNG ODER BEDINGUNGEN -
 * ausdrücklich oder stillschweigend - verbreitet.
 * Die sprachspezifischen Genehmigungen und Beschränkungen
 * unter der Lizenz sind dem Lizenztext zu entnehmen.
 */
import {
  SettingItem,
  SettingListResource,
  SettingListResourceService,
  SettingName,
  SettingsService,
} from '@administration-client/settings-shared';
import {
  ApiBasedService,
  ListResourceServiceConfig,
  ResourceLinkRel,
  ResourceServiceConfig,
  StateResource,
  tapOnLoadedResource,
} from '@common/tech-shared';
import { inject, Injectable } from '@angular/core';
import { Resource } from '@ngxp/rest';
import { map, Observable } from 'rxjs';
import { LoeschOrganisationEinheit, LoeschOrganisationEinheitResource } from './loesch-organisations-einheit.model';

@Injectable({ providedIn: 'root' })
export class LoeschOrganisationEinheitService extends ApiBasedService<
  LoeschOrganisationEinheitResource,
  LoeschOrganisationEinheitResource,
  Resource,
  SettingListResource,
  LoeschOrganisationEinheitResource,
  LoeschOrganisationEinheit
> {
  private readonly settingListResourceService: SettingListResourceService = inject(SettingListResourceService);

  override getResourceConfig(): ResourceServiceConfig<LoeschOrganisationEinheitResource> {
    const settingsService: SettingsService = inject(SettingsService);
    return {
      baseResource: settingsService.get<LoeschOrganisationEinheit>(SettingName.LOESCH_ORGANISATION_EINHEIT),
      getLinkRel: ResourceLinkRel.SELF,
      edit: { linkRel: ResourceLinkRel.SELF },
      delete: { linkRel: ResourceLinkRel.SELF },
    };
  }

  override getListResourceConfig(): ListResourceServiceConfig<Resource> {
    return undefined;
  }

  override getFeatureName(): string {
    return 'LoeschOrganisationEinheit';
  }

  public override save(
    loeschOrganisationEinheit: LoeschOrganisationEinheit,
  ): Observable<StateResource<LoeschOrganisationEinheitResource>> {
    return this._getResourceService()
      .save(this.buildSettingItem(loeschOrganisationEinheit))
      .pipe(tapOnLoadedResource(() => this.settingListResourceService.refresh()));
  }

  public override create<R>(toCreate: R): Observable<StateResource<LoeschOrganisationEinheitResource>> {
    return this.settingListResourceService.create(this.buildSettingItem(toCreate)).pipe(
      map((created) => created as StateResource<LoeschOrganisationEinheitResource>),
      tapOnLoadedResource(() => this.settingListResourceService.refresh()),
    );
  }

  public override delete(): Observable<StateResource<LoeschOrganisationEinheitResource>> {
    return super.delete().pipe(tapOnLoadedResource(() => this.settingListResourceService.refresh()));
  }

  public refresh(): void {
    this.settingListResourceService.refresh();
  }

  private buildSettingItem<T>(settingBody: T): SettingItem<T> {
    return { name: SettingName.LOESCH_ORGANISATION_EINHEIT, settingBody };
  }
}
